import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Loader2, Package, Download, Trash2, AlertTriangle, ShieldCheck, ShieldOff, Send, FileSpreadsheet } from 'lucide-react';
import { LogSubmissionDialog } from './SubmissionLog';
import { PortalRoundTrip } from './PortalRoundTrip';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

/** Phase 4 — build versioned submission ZIPs (letter, consolidated response, annex workbooks, cost summary, attachments, manifest). */
export function PackageCard({ project, onRefresh }) {
  const pid = project.id;
  const packages = [...(project.packages || [])].reverse();
  const [building, setBuilding] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [submitPkg, setSubmitPkg] = useState(null);
  const items = project.deliverables || [];
  const [ready, setReady] = useState(null);
  useEffect(() => {
    if (!items.length) return;
    axios.get(`${API_URL}/api/rfp/response/projects/${pid}/readiness`, { withCredentials: true }).then(r => setReady(r.data)).catch(() => setReady(null));
  }, [pid, project.deliverables, project.documents, project.commercials]); // eslint-disable-line react-hooks/exhaustive-deps
  const waive = async (b) => {
    const reason = window.prompt(`Waive "${b.title}" for this submission? Give the reason (recorded in the manifest):`);
    if (reason === null) return;
    try { await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${b.id}`, { waive_reason: reason }, { withCredentials: true }); toast.success(`"${b.title}" waived`); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not waive'); }
  };
  const unwaive = async (w) => {
    try { await axios.put(`${API_URL}/api/rfp/response/projects/${pid}/deliverables/${w.id}`, { waive_reason: '' }, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not restore'); }
  };
  const build = async () => {
    setBuilding(true);
    try {
      const r = await axios.post(`${API_URL}/api/rfp/response/projects/${pid}/package`, {}, { withCredentials: true });
      toast.success(`Package v${r.data.version} built — ${r.data.stats.files} files`);
      onRefresh();
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === 'object' ? det.message : det || 'Package build failed');
    } finally { setBuilding(false); }
  };
  const download = async (pk) => {
    setDownloading(pk.id);
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/projects/${pid}/packages/${pk.id}/download`, { withCredentials: true, responseType: 'blob' });
      saveAs(r.data, pk.filename);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(''); }
  };
  const remove = async (pk) => {
    if (!window.confirm(`Delete package v${pk.version}?`)) return;
    try { await axios.delete(`${API_URL}/api/rfp/response/projects/${pid}/packages/${pk.id}`, { withCredentials: true }); onRefresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not delete'); }
  };
  const pasteBack = async (fmt) => {
    try {
      const r = await axios.get(`${API_URL}/api/rfp/response/projects/${pid}/portal-export`, { params: { format: fmt }, withCredentials: true, responseType: 'blob' });
      saveAs(r.data, `${project.name} - Portal paste-back.${fmt}`);
      toast.success('Question / Answer export ready — paste it straight into the portal');
    } catch (err) {
      let det = err?.response?.data?.detail;
      if (err?.response?.data instanceof Blob) { try { det = JSON.parse(await err.response.data.text()).detail; } catch { /* ignore */ } }
      toast.error(det || 'Export failed');
    }
  };
  if (!items.length) return null;
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4 space-y-3" data-testid="rfp-package">
      {project.response_style === 'portal_qa' && (
        <div className="flex items-center gap-2 flex-wrap bg-cyan-50/60 border border-cyan-100 rounded px-3 py-2" data-testid="rfp-portal-pasteback">
          <FileSpreadsheet className="w-4 h-4 text-cyan-700" />
          <span className="text-xs font-semibold text-[#0F2040]">Portal paste-back</span>
          <span className="text-[10px] text-slate-500">Two columns — Question, Answer — in the portal's exact order (plus a sheet with section and number). Also included in the ZIP.</span>
          <span className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => pasteBack('xlsx')} data-testid="rfp-portal-pasteback-xlsx"><Download className="w-3 h-3 mr-1" />XLSX</Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => pasteBack('csv')} data-testid="rfp-portal-pasteback-csv"><Download className="w-3 h-3 mr-1" />CSV</Button>
          </span>
        </div>
      )}
      <PortalRoundTrip project={project} onRefresh={onRefresh} />
      <div className="flex items-center gap-2 flex-wrap">
        <Package className="w-4 h-4 text-[#4ABFCF]" />
        <span className="text-sm font-semibold text-[#0F2040]">Submission package</span>
        <span className="text-[10px] text-slate-500" data-testid="rfp-package-style-note">{project.response_style === 'portal_qa'
          ? 'Portal Q&A export: ZIP: letter · ONE response in the portal\u2019s question order (question + answer pairs, ready to paste back) · Excel companion per section · cost summary · attachments · manifest'
          : project.response_style === 'single_document'
          ? 'Single Document Request: ZIP: letter · ONE response mirroring the client\u2019s outline & numbering (matrices embedded, submission checklist) · Excel companion per matrix · cost summary · attachments · manifest'
          : 'ZIP: letter · consolidated response (TOC) · one workbook per annex · cost summary · attachments · manifest + checklist'}</span>
        {ready && (ready.ready
          ? <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="rfp-package-ready"><ShieldCheck className="w-2.5 h-2.5 mr-1" />Ready to package{ready.waived.length ? ` · ${ready.waived.length} waived` : ''}</Badge>
          : <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="rfp-package-notdone"><AlertTriangle className="w-2.5 h-2.5 mr-1" />{ready.blockers.length} item{ready.blockers.length === 1 ? '' : 's'} blocking</Badge>)}
        <Button size="sm" onClick={build} disabled={building || (ready && !ready.ready)} title={ready && !ready.ready ? 'Finish or waive every checklist item first' : ''} className="ml-auto h-7 text-[11px] bg-[#0F2040] hover:bg-[#1A3055] text-white" data-testid="rfp-package-build-btn">
          {building ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Package className="w-3 h-3 mr-1" />}{building ? 'Building…' : `Build package v${(project.packages || []).length + 1}`}
        </Button>
      </div>
      {ready && !ready.ready && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-md divide-y divide-amber-100" data-testid="rfp-package-blockers">
          {ready.blockers.map(b => (
            <div key={b.id} className="p-2 flex items-center gap-2 text-xs" data-testid={`rfp-package-blocker-${b.id}`}>
              <Badge variant="outline" className="text-[9px] capitalize bg-white">{b.type}</Badge>
              <span className="font-medium text-[#0F2040]">{b.title}</span>
              <span className="text-[10px] text-amber-700">{b.detail || 'not marked done'}</span>
              <button type="button" onClick={() => waive(b)} className="ml-auto text-[10px] text-slate-500 hover:text-[#0F2040] inline-flex items-center gap-1" data-testid={`rfp-package-waive-${b.id}`}><ShieldOff className="w-3 h-3" />Waive…</button>
            </div>
          ))}
        </div>
      )}
      {ready && ready.waived.length > 0 && (
        <div className="text-[10px] text-slate-500 space-y-0.5" data-testid="rfp-package-waived">
          {ready.waived.map(w => <p key={w.id}>Waived: <b>{w.title}</b> — {w.reason} <span className="text-slate-400">({w.by})</span> <button type="button" onClick={() => unwaive(w)} className="text-[#4ABFCF] hover:underline ml-1" data-testid={`rfp-package-unwaive-${w.id}`}>restore</button></p>)}
        </div>
      )}
      <LogSubmissionDialog project={project} pkg={submitPkg} open={!!submitPkg} onOpenChange={v => { if (!v) setSubmitPkg(null); }} onLogged={onRefresh} />
      {packages.length > 0 && (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md" data-testid="rfp-package-list">
          {packages.map(pk => (
            <div key={pk.id} className="p-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`rfp-package-${pk.id}`}>
              <Badge variant="outline" className="text-[9px] bg-[#0F2040] text-white border-[#0F2040]">v{pk.version}</Badge>
              <span className="font-medium text-[#0F2040]">{pk.filename}</span>
              <span className="text-[10px] text-slate-400">{fmtSize(pk.size)} · {pk.stats?.files} files{pk.stats?.has_commercials ? ' · cost summary' : ''} · {new Date(pk.created_at).toLocaleString()} · {pk.created_by}</span>
              {pk.stats?.waived > 0 && <span className="text-[10px] text-amber-600">{pk.stats.waived} waived</span>}
              <span className="ml-auto flex items-center gap-1.5">
                {pk.submitted_at && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`rfp-package-submitted-${pk.id}`}>submitted {String(pk.submitted_at).slice(0, 10)}</Badge>}
                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setSubmitPkg(pk)} data-testid={`rfp-package-submit-${pk.id}`}><Send className="w-3 h-3 mr-1" />Log submission</Button>
                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={downloading === pk.id} onClick={() => download(pk)} data-testid={`rfp-package-dl-${pk.id}`}>
                  {downloading === pk.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Download className="w-3 h-3 mr-1" />ZIP</>}
                </Button>
                <button type="button" onClick={() => remove(pk)} className="text-slate-300 hover:text-rose-500" title="Delete" data-testid={`rfp-package-del-${pk.id}`}><Trash2 className="w-3 h-3" /></button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
